import { FormEvent } from "react";
import { useRouter } from "next/router";
import { IExtendedSearchData } from "src/api/server/extended-search";
import { MobileSearchFilterContainer } from "src/components/pages/search/components/filter-section/mobile-search-filter/mobile-search-filter-container";
import { SearchFilterContainer } from "src/components/pages/search/components/filter-section/search-filter/search-filter-container";
import { useFilters } from "src/components/pages/search/hooks/use-filters";
import { ISearch } from "src/components/pages/search/search.types";
import { handleIsInvestable, handleSearch, setDefaultFilters, setQuery } from "src/components/pages/search/store/actions";
import { getNormalizedRouterQuery } from "src/components/pages/search/utils/url-params-helpers";
import { useRouterLocale } from "src/utils/router/use-router-locale";
import { sanitizeSearchTerm } from "src/utils/sanitize-search-term";

interface IFilterSectionProps extends ISearch {
    data: IExtendedSearchData;
    resultCount: number | null;
    isLoading: boolean;
    onReset: () => void;
}

export const FilterSection = ({ data, resultCount, isLoading, onSearch, onReset }: IFilterSectionProps) => {
    const router = useRouter();
    const { country } = useRouterLocale();

    const { searchTerm } = getNormalizedRouterQuery(router.query);
    const sanitizedSearchTerm = searchTerm && sanitizeSearchTerm(searchTerm);

    const { tagsData, keyFiguresData } = data;
    const { popularKeyFiguresGroup, keyFiguresGroupsWithIsPopularIndication, popularTags } = useFilters(tagsData, keyFiguresData);

    const onChangeInvestable = (event: FormEvent<HTMLInputElement>) => {
        const isChecked = event.currentTarget.checked;
        handleIsInvestable({ isChecked, onSearch });
    };

    const handleOnSearchTermClear = () => {
        const clonedQuery = { ...router.query };
        delete clonedQuery.searchTerm;
        delete clonedQuery.country;

        if (!clonedQuery.sortBy) {
            const { defaultSelectedRanking } = data.rankingsData;
            const ranking = { id: defaultSelectedRanking.id, sortDirection: defaultSelectedRanking.sortDirection };

            setDefaultFilters(ranking, country);
        }
        setQuery("");

        router.replace({ pathname: window.location.pathname, query: clonedQuery, href: window.location.href }, undefined, { shallow: true });
        handleSearch({ onSearch });
    };

    return (
        <>
            <MobileSearchFilterContainer
                searchTerm={sanitizedSearchTerm}
                resultCount={resultCount}
                isLoading={isLoading}
                tagsData={tagsData}
                popularKeyFiguresGroup={popularKeyFiguresGroup}
                keyFiguresGroupsWithIsPopularIndication={keyFiguresGroupsWithIsPopularIndication}
                popularTags={popularTags}
                onSearchTermClear={handleOnSearchTermClear}
                onReset={onReset}
                onSearch={onSearch}
            />
            <SearchFilterContainer
                searchTerm={sanitizedSearchTerm}
                resultCount={resultCount}
                popularKeyFiguresGroup={popularKeyFiguresGroup}
                popularTags={popularTags}
                keyFiguresData={{ ...keyFiguresData, keyFiguresGroups: keyFiguresGroupsWithIsPopularIndication }}
                tagsData={tagsData}
                onSearchTermClear={handleOnSearchTermClear}
                onChangeInvestable={onChangeInvestable}
                onReset={onReset}
                onSearch={onSearch}
            />
        </>
    );
};
